import { z } from 'zod';

/**
 * What a single-use account link is for. A token is only ever consumed for the purpose it was
 * issued under, so a verification link cannot be replayed as a password reset.
 *
 * - `verify_email` — proves the account's address is a mailbox its owner reads.
 * - `reset_password` — lets the mailbox owner choose a new password while signed out.
 */
export const ACCOUNT_TOKEN_PURPOSES = ['verify_email', 'reset_password'] as const;
export const accountTokenPurposeSchema = z.enum(ACCOUNT_TOKEN_PURPOSES);
export type AccountTokenPurpose = z.infer<typeof accountTokenPurposeSchema>;

/**
 * Every message Sigora sends. One outbox row per message carries its kind, which is also what the
 * per-address sending limits count by.
 *
 * - `verify_email` — the link that verifies a new account.
 * - `password_reset` — the link that resets a password.
 * - `account_exists` — sent in place of a verification link when someone signs up with an address
 *   that already has a verified account, so the signup answer never says which addresses do.
 */
export const MAIL_KINDS = ['verify_email', 'password_reset', 'account_exists'] as const;
export const mailKindSchema = z.enum(MAIL_KINDS);
export type MailKind = z.infer<typeof mailKindSchema>;

/**
 * Where an outbox row is in delivery.
 *
 * - `pending` — written, and its delivery job not yet finished.
 * - `sent` — handed to the transport, which accepted it.
 * - `failed` — the delivery job exhausted its retries.
 */
export const MAIL_STATUSES = ['pending', 'sent', 'failed'] as const;
export const mailStatusSchema = z.enum(MAIL_STATUSES);
export type MailStatus = z.infer<typeof mailStatusSchema>;

/**
 * The password a person may choose, at signup and at a reset: at least twelve characters and at
 * most 1024, with no rule about what they are made of. Length is what makes a password costly to
 * guess, and composition rules mostly produce the same few predictable substitutions.
 *
 * Signing in checks only the ceiling, so an account whose password predates the floor still signs
 * in. The ceiling is shared with sign-in and with `pnpm user:set-password`: a password longer than
 * sign-in accepts would be stored and then refused on every attempt, an account nobody can use
 * with nothing saying why. Lengths are counted the way JavaScript counts a string, in UTF-16 code
 * units, on the server and in the SPA alike.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 1024;

/**
 * A string whose length, counted in UTF-16 code units as `String.prototype.length` counts it, is
 * between two bounds. Zod's own `.min()` and `.max()` count code points, so an emoji is one
 * character to them and two to the SPA and to `pnpm user:set-password`; this raises the same
 * `too_small` and `too_big` issues those checks raise, counted the way the other two count.
 */
const utf16LengthBetween = (minimum: number, maximum: number) =>
  z.string().superRefine((value, context) => {
    if (value.length < minimum) {
      context.addIssue({
        code: 'too_small',
        origin: 'string',
        minimum,
        inclusive: true,
        input: value,
      });
    } else if (value.length > maximum) {
      context.addIssue({
        code: 'too_big',
        origin: 'string',
        maximum,
        inclusive: true,
        input: value,
      });
    }
  });

export const newPasswordSchema = utf16LengthBetween(MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH);

/** What sign-in takes as a password: anything non-empty up to the ceiling, floor or no floor. */
export const signInPasswordSchema = utf16LengthBetween(1, MAX_PASSWORD_LENGTH);

/**
 * The display name an account carries: what a stakeholder sees an account called. Trimmed, one to
 * eighty characters, and none of Unicode's "other" categories — control characters, formatting
 * marks (the bidirectional overrides and isolates among them), surrogates, private use and the
 * unassigned — since a name is shown to people other than the one who typed it, and a
 * bidirectional override or a NUL in it is a way to make it read as something it is not.
 *
 * Two formatting marks are the exception: the zero-width non-joiner (U+200C) and joiner (U+200D).
 * They are how ordinary names are spelled in Persian and in Indic and other scripts — a Persian
 * name routinely carries a ZWNJ, a Devanagari conjunct a ZWJ — and neither can reorder or hide
 * text. `DISPLAY_NAME_FORBIDDEN` matches any other "other" character, and a name it matches
 * anywhere is refused. A name made only of joiners is refused too (`DISPLAY_NAME_JOINERS_ONLY`),
 * since it renders as nothing. The SPA applies the same two expressions, so the two cannot
 * disagree.
 */
export const MIN_DISPLAY_NAME_LENGTH = 1;
export const MAX_DISPLAY_NAME_LENGTH = 80;
export const DISPLAY_NAME_FORBIDDEN = /(?![\u200C\u200D])\p{C}/u;
export const DISPLAY_NAME_JOINERS_ONLY = /^[\u200C\u200D]+$/u;
export const displayNameSchema = z
  .string()
  .trim()
  .min(MIN_DISPLAY_NAME_LENGTH)
  .max(MAX_DISPLAY_NAME_LENGTH)
  .refine((name) => !DISPLAY_NAME_FORBIDDEN.test(name), {
    message: 'a display name may not contain control or formatting characters',
  })
  .refine((name) => !DISPLAY_NAME_JOINERS_ONLY.test(name), {
    message: 'a display name needs at least one visible character',
  });
